import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Archive, History, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatApiError } from "../lib/format";
import { DIVISIONS, isManagement } from "../lib/constants";

const emptyProduct = { name: "", division_id: "", kind: "product", category: "", description: "", price: 0, unit: "package", included: [], specification: "", client_visible: true, active: true };
const emptyPackage = { name: "", division_id: null, kind: "package", category: "", description: "", price: 0, unit: "package", included: [], specification: "", client_visible: true, active: true };

export default function Pricelist() {
  const { user } = useAuth();
  const mgmt = isManagement(user);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [productOpen, setProductOpen] = useState(false);
  const [packageOpen, setPackageOpen] = useState(false);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [pkgForm, setPkgForm] = useState(emptyPackage);
  const [historyDlg, setHistoryDlg] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => Promise.all([
    api.get("/products?kind=product").then(({ data }) => setProducts(data)),
    api.get("/products?kind=package").then(({ data }) => setPackages(data)),
  ]);
  useEffect(() => { load(); }, []);

  const submitProduct = async () => {
    try {
      await api.post("/products", { ...prodForm, price: Number(prodForm.price) });
      toast.success("Produk ditambahkan"); setProductOpen(false); setProdForm(emptyProduct); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitPackage = async () => {
    try {
      await api.post("/products", { ...pkgForm, price: Number(pkgForm.price) });
      toast.success("Paket ditambahkan"); setPackageOpen(false); setPkgForm(emptyPackage); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const archive = async (id) => {
    if (!window.confirm("Nonaktifkan produk ini?")) return;
    await api.delete(`/products/${id}`); toast.success("Diarsipkan"); load();
  };
  const showHistory = async (p) => {
    setHistoryDlg(p);
    const { data } = await api.get(`/products/${p.id}/history`);
    setHistory(data);
  };
  const toggleInclude = (div_id) => {
    setPkgForm((f) => {
      const has = f.included.some((x) => x.division_id === div_id);
      return { ...f, included: has ? f.included.filter((x) => x.division_id !== div_id)
        : [...f.included, { division_id: div_id, description: `Layanan ${DIVISIONS.find(d=>d.id===div_id)?.short}`, quantity: 1 }] };
    });
  };

  return (
    <div className="space-y-6" data-testid="pricelist-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Master Produk & Pricelist</h1>
        <p className="text-sm text-[#5C6066] mt-1">Paket DWG lintas divisi + pricelist tiap divisi.</p>
      </div>
      <Tabs defaultValue="packages">
        <TabsList className="bg-white border border-[#E5E0D8]">
          <TabsTrigger value="packages" data-testid="tab-packages">Paket DWG</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produk / Layanan Divisi</TabsTrigger>
        </TabsList>

        <TabsContent value="packages" className="mt-4 space-y-4">
          {mgmt && (
            <div className="flex justify-end">
              <Dialog open={packageOpen} onOpenChange={setPackageOpen}>
                <DialogTrigger asChild><Button className="bg-brand text-white" data-testid="btn-add-package"><Plus className="w-4 h-4 mr-1.5" /> Paket Baru</Button></DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Paket DWG Multi-Divisi</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3 py-2">
                    <div className="col-span-2"><Label>Nama Paket</Label><Input className="mt-1.5" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} /></div>
                    <div><Label>Kategori</Label>
                      <Select value={pkgForm.category} onValueChange={(v) => setPkgForm({ ...pkgForm, category: v })}>
                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih" /></SelectTrigger>
                        <SelectContent>{["ESSENTIAL","OUTDOOR","INDOOR","MAKEUP","OTHER"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Harga (Rp)</Label><Input type="number" className="mt-1.5" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Deskripsi</Label><Textarea rows={2} className="mt-1.5" value={pkgForm.description} onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })} /></div>
                    <div className="col-span-2">
                      <Label>Divisi yang Disertakan</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {DIVISIONS.map((d) => (
                          <label key={d.id} className="flex items-center gap-2 p-2 rounded border border-[#E5E0D8] bg-white cursor-pointer hover:bg-[#FAF6EE]">
                            <Checkbox checked={pkgForm.included.some((x) => x.division_id === d.id)} onCheckedChange={() => toggleInclude(d.id)} />
                            <div className="text-xs">{d.short}</div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPackageOpen(false)}>Batal</Button>
                    <Button className="bg-brand text-white" onClick={submitPackage} disabled={!pkgForm.name} data-testid="btn-save-package">Simpan</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-[#8C9096] bg-[#FAF6EE]">
                    <th className="text-left px-5 py-3">Nama</th>
                    <th className="text-left px-4 py-3">Kategori</th>
                    <th className="text-left px-4 py-3">Divisi Terlibat</th>
                    <th className="text-right px-4 py-3">Harga</th>
                    <th className="text-center px-4 py-3">Aktif</th>
                    <th className="text-right px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#8C9096]">Belum ada paket.</td></tr>}
                  {packages.map((p) => (
                    <tr key={p.id} className={`border-t border-[#F0EBE0] hover:bg-[#F7F4EE] ${!p.active && "opacity-50"}`} data-testid={`package-row-${p.id}`}>
                      <td className="px-5 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-[#5C6066]">{p.category}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(p.included || []).map((inc, i) => {
                            const div = DIVISIONS.find((d) => d.id === inc.division_id);
                            return div ? <Badge key={i} variant="outline" className="text-[10px]">{div.short}</Badge> : null;
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatIDR(p.price)}</td>
                      <td className="px-4 py-3 text-center">{p.active ? "✓" : "—"}</td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => showHistory(p)}><History className="w-4 h-4" /></Button>
                        {mgmt && p.active && <Button size="sm" variant="ghost" onClick={() => archive(p.id)} data-testid={`btn-archive-${p.id}`}><Archive className="w-4 h-4" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={productOpen} onOpenChange={setProductOpen}>
              <DialogTrigger asChild><Button className="bg-brand text-white" data-testid="btn-add-product"><Plus className="w-4 h-4 mr-1.5" /> Produk Baru</Button></DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Produk / Layanan Divisi</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 py-2">
                  <div className="col-span-2"><Label>Nama</Label><Input className="mt-1.5" value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} /></div>
                  <div><Label>Divisi</Label>
                    <Select value={prodForm.division_id} onValueChange={(v) => setProdForm({ ...prodForm, division_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih" /></SelectTrigger>
                      <SelectContent>{DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Kategori</Label><Input className="mt-1.5" value={prodForm.category} onChange={(e) => setProdForm({ ...prodForm, category: e.target.value })} /></div>
                  <div><Label>Harga (Rp)</Label><Input type="number" className="mt-1.5" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} /></div>
                  <div><Label>Unit</Label>
                    <Select value={prodForm.unit} onValueChange={(v) => setProdForm({ ...prodForm, unit: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{["package","person","pax","hour","day","item","crew","set"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Deskripsi</Label><Textarea rows={2} className="mt-1.5" value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setProductOpen(false)}>Batal</Button>
                  <Button className="bg-brand text-white" onClick={submitProduct} disabled={!prodForm.name || !prodForm.division_id} data-testid="btn-save-product">Simpan</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-[#8C9096] bg-[#FAF6EE]">
                  <th className="text-left px-5 py-3">Nama</th>
                  <th className="text-left px-4 py-3">Divisi</th>
                  <th className="text-left px-4 py-3">Kategori</th>
                  <th className="text-right px-4 py-3">Harga</th>
                  <th className="text-left px-4 py-3">Unit</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const div = DIVISIONS.find((d) => d.id === p.division_id);
                  return (
                    <tr key={p.id} className={`border-t border-[#F0EBE0] hover:bg-[#F7F4EE] ${!p.active && "opacity-50"}`} data-testid={`product-row-${p.id}`}>
                      <td className="px-5 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3">{div && <Badge variant="outline" className="text-[10px]">{div.short}</Badge>}</td>
                      <td className="px-4 py-3 text-[#5C6066]">{p.category}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatIDR(p.price)}</td>
                      <td className="px-4 py-3 text-[#5C6066]">{p.unit}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!historyDlg} onOpenChange={(o) => !o && setHistoryDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Riwayat Harga — {historyDlg?.name}</DialogTitle></DialogHeader>
          <div className="divide-y divide-[#F0EBE0] max-h-[400px] overflow-y-auto">
            {history.length === 0 && <div className="p-6 text-sm text-center text-[#8C9096]">Belum ada perubahan harga.</div>}
            {history.map((h) => (
              <div key={h.id} className="py-3 text-sm">
                <div className="font-mono">{formatIDR(h.old_price)} → {formatIDR(h.new_price)}</div>
                <div className="text-xs text-[#8C9096]">{h.changed_by_name} • {formatDate(h.created_at)} • {h.reason || "-"}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
